import React, { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, StatusPill, Tabs } from "../../components/ui";
import { Icon } from "../shared/components";
import {
  ensureArray, ensureObject, formatTime, formatMoney,
  splitCommaTags, getInitials, timelineEventTitle, timelineEventTone,
  formatBrazilPhone, normalizeBrazilPhone, maskBrazilPhoneInput,
  conversationStatusLabel, getSlaMeta,
} from "../shared/utils";

function InlineFieldEditor({ label, value, placeholder, onChange, onSave, saving = false }) {
  return (
    <div className="inlineFieldEditor">
      <label>{label}</label>
      <div className="inlineFieldControl">
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        <button type="button" className="miniInlineSave" onClick={onSave} disabled={saving}>
          {saving ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </div>
  );
}

function Info({ label, value }) { return <div className="infoItem"><span>{label}</span><strong>{value}</strong></div>; }

function statusBadgeTone(status) {
  if (status === "atendimento") return "warning";
  if (status === "finalizado")  return "neutral";
  if (status === "novo")        return "info";
  return "neutral";
}

function CustomerSidePanel({ api, user, conversation, contact, messages = [], users, refreshToken, onReload, onNavigate, onArchive }) {
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("contact");
  const [stages, setStages] = useState([]);
  const [notice, setNotice] = useState("");
  const [contactForm, setContactForm] = useState({ name: "", phone: "", company: "", email: "", tags: "", notes: "" });
  const [leadForm, setLeadForm] = useState({ title: "", value: "", stage_id: "", responsible_user_id: "" });
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [internalNote, setInternalNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [savingInlineField, setSavingInlineField] = useState("");
  const [historyFilter, setHistoryFilter] = useState("all");
  const [addingTag, setAddingTag] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [savingTag, setSavingTag] = useState(false);
  const tagInputRef = useRef(null);

  useEffect(() => {
    if (contact) {
      setContactForm({
        name: contact.name || "",
        phone: maskBrazilPhoneInput(contact.phone || ""),
        company: contact.company || "",
        email: contact.email || "",
        tags: contact.tags || "",
        notes: contact.notes || "",
      });
      setLeadForm((old) => ({ ...old, title: `Oportunidade - ${contact.name || ""}` }));
    }
  }, [contact?.id]);

  useEffect(() => {
    setActiveTab("contact");
    setEditing(false);
    setHistoryFilter("all");
  }, [conversation?.id]);

  useEffect(() => {
    api.get("/funnel/stages").then((response) => {
      setStages(response.data);
      if (response.data[0]) setLeadForm((old) => ({ ...old, stage_id: old.stage_id || response.data[0].id }));
    });
  }, []);

  useEffect(() => {
    if (!conversation?.id) {
      setTimeline([]);
      return;
    }
    loadTimeline(conversation.id);
  }, [conversation?.id, refreshToken]);

  async function loadTimeline(conversationId) {
    if (!conversationId) return;
    setTimelineLoading(true);
    try {
      const response = await api.get(`/conversations/${conversationId}/timeline`);
      setTimeline(response.data || []);
    } catch {
      setTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  }

  function setContactField(field, value) {
    setContactForm((old) => ({ ...old, [field]: value }));
  }

  function setLeadField(field, value) {
    setLeadForm((old) => ({ ...old, [field]: value }));
  }

  async function saveContact(e) {
    e.preventDefault();
    if (!contact) return;
    await api.put(`/contacts/${contact.id}`, {
      ...contactForm,
      phone: normalizeBrazilPhone(contactForm.phone),
    });
    setEditing(false);
    setNotice("Contato atualizado.");
    await onReload();
  }

  async function saveInlineContactField(field) {
    if (!contact || savingInlineField) return;
    setSavingInlineField(field);
    setNotice("");
    try {
      await api.put(`/contacts/${contact.id}`, {
        ...contactForm,
        phone: normalizeBrazilPhone(contactForm.phone),
      });
      setNotice(`Campo ${field === "notes" ? "observações" : field} atualizado.`);
      await onReload();
    } catch (err) {
      setNotice(err.response?.data?.error || "Não foi possível atualizar o contato.");
    } finally {
      setSavingInlineField("");
    }
  }

  async function addTag() {
    const newTag = tagInput.trim().toLowerCase();
    if (!newTag || !contact || savingTag) return;
    const existing = splitCommaTags(contact.tags);
    if (existing.includes(newTag)) { setTagInput(""); setAddingTag(false); return; }
    const merged = [...existing, newTag].join(", ");
    setSavingTag(true);
    try {
      await api.put(`/contacts/${contact.id}`, {
        name: contact.name, phone: contact.phone, company: contact.company,
        email: contact.email, tags: merged, notes: contact.notes,
      });
      setTagInput("");
      setAddingTag(false);
      await onReload();
    } catch {
      setNotice("Não foi possível salvar a tag.");
    } finally {
      setSavingTag(false);
    }
  }

  async function removeTag(tag) {
    if (!contact) return;
    const updated = splitCommaTags(contact.tags).filter((t) => t !== tag).join(", ");
    try {
      await api.put(`/contacts/${contact.id}`, {
        name: contact.name, phone: contact.phone, company: contact.company,
        email: contact.email, tags: updated, notes: contact.notes,
      });
      await onReload();
    } catch {
      setNotice("Não foi possível remover a tag.");
    }
  }

  async function saveInternalNote(e) {
    e.preventDefault();
    if (!conversation || !internalNote.trim() || savingNote) return;
    setSavingNote(true);
    setNotice("");
    try {
      await api.post(`/conversations/${conversation.id}/notes`, {
        note: internalNote,
      });
      setInternalNote("");
      setNotice("Nota interna registrada.");
      await loadTimeline(conversation.id);
    } catch (err) {
      setNotice(err.response?.data?.error || "Não foi possível salvar a nota interna.");
    } finally {
      setSavingNote(false);
    }
  }

  async function createLead(e) {
    e.preventDefault();
    if (!contact) return;
    await api.post("/leads", {
      contact_id: contact.id,
      stage_id: leadForm.stage_id ? Number(leadForm.stage_id) : null,
      title: leadForm.title || `Oportunidade - ${contact.name}`,
      value: Number(leadForm.value || 0),
      responsible_user_id: leadForm.responsible_user_id ? Number(leadForm.responsible_user_id) : user.id,
    });
    setLeadForm({ title: `Oportunidade - ${contact.name}`, value: "", stage_id: stages[0]?.id || "", responsible_user_id: "" });
    setNotice("Oportunidade criada no funil.");
    if (conversation?.id) await loadTimeline(conversation.id);
    await onReload();
  }

  const customerSla = useMemo(() => getSlaMeta(conversation), [conversation]);
  const tagItems = useMemo(
    () => splitCommaTags(contact?.tags),
    [contact?.tags]
  );
  const messageHistory = useMemo(() => (
    (messages || []).map((message) => ({
      id: `message-${message.id}`,
      kind: "message",
      subtype: message.sender_type === "cliente" ? "incoming" : "outgoing",
      title: message.sender_type === "cliente" ? "Mensagem do cliente" : "Mensagem do atendimento",
      body: message.body_text || (message.media_url ? "Mídia enviada no atendimento." : "Mensagem sem texto."),
      created_at: message.created_at,
      actor_name: message.sender_type === "cliente" ? (contact?.name || conversation?.contact_name || "Cliente") : (message.sender_name || user.name || "Atendimento"),
      meta: message.status || "",
    }))
  ), [messages, contact?.name, conversation?.contact_name, user.name]);
  const eventHistory = useMemo(() => (
    (timeline || []).map((event) => ({
      id: `event-${event.id}`,
      kind: event.event_type === "note" ? "note" : "event",
      subtype: event.event_type,
      title: timelineEventTitle(event.event_type),
      body: event.body,
      created_at: event.created_at,
      actor_name: event.actor_name || "Sistema",
      meta: "",
      tone: timelineEventTone(event.event_type),
    }))
  ), [timeline]);
  const unifiedHistory = useMemo(() => (
    [...messageHistory, ...eventHistory]
      .filter((entry) => {
        if (historyFilter === "messages") return entry.kind === "message";
        if (historyFilter === "events") return entry.kind === "event";
        if (historyFilter === "notes") return entry.kind === "note";
        return true;
      })
      .sort((left, right) => new Date(right.created_at || 0).getTime() - new Date(left.created_at || 0).getTime())
  ), [eventHistory, historyFilter, messageHistory]);

  if (!conversation) {
    return (
      <div className="rightPanel customerPanel">
        <div className="crmPanelEmpty">
          <h3>CRM do atendimento</h3>
          <p className="muted">Selecione uma conversa para ver contato, histórico e oportunidade.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rightPanel customerPanel">
      {/* ── Tab bar only — no title ── */}
      <div className="crmPanelTop">
        <nav className="subnav" style={{ borderBottom: "none" }}>
          <div className="subnav-tabs">
            {[
              { value: "contact", label: "Contato" },
              { value: "crm", label: "CRM" },
              { value: "history", label: "Histórico" },
            ].map((item) => (
              <button
                key={item.value}
                type="button"
                className={`subnav-tab${activeTab === item.value ? " active" : ""}`}
                onClick={() => setActiveTab(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </nav>
      </div>

      {notice && <div className="noticeBox compact">{notice}</div>}

      <div className="crmScrollBody">
      {!contact ? (
        <p className="muted" style={{ padding: "16px 14px", fontSize: "var(--text-xs)", color: "var(--ink-4)" }}>Carregando dados do contato...</p>
      ) : (
        <>
          {activeTab === "contact" && !editing ? (
            <>
              {/* ── Hero: avatar centrado + nome + telefone + ações ── */}
              <div className="crmHero">
                <Avatar name={contact.name} size="lg" src={contact.avatar_url} />
                <span className="crmHero-name">{contact.name}</span>
                <span className="crmHero-phone">{formatBrazilPhone(contact.phone || "") || contact.phone || "—"}</span>
                <div className="crmHero-actions">
                  <button className="crmHero-action" title="Ligar para cliente"><Icon name="phone" size={14} /></button>
                  <button className="crmHero-action" title="Enviar e-mail"><Icon name="mail" size={14} /></button>
                  <button className="crmHero-action" title="Editar contato" onClick={() => setEditing(true)}><Icon name="edit" size={14} /></button>
                </div>
              </div>

              {/* ── CLIENTE ── */}
              <div className="crmSection">
                <div className="crmSection-label">Cliente</div>
                <div className="crmRow"><span className="crmRow-key">Nome</span><span className="crmRow-val">{contact.name}</span></div>
                <div className="crmRow"><span className="crmRow-key">Empresa</span><span className="crmRow-val">{contact.company || "Não informado"}</span></div>
                <div className="crmRow"><span className="crmRow-key">Email</span><span className="crmRow-val">{contact.email || "Não informado"}</span></div>
                <div className="crmRow"><span className="crmRow-key">Telefone</span><span className="crmRow-val">{formatBrazilPhone(contact.phone || "") || contact.phone || "—"}</span></div>
              </div>

              {/* ── ATENDIMENTO ── */}
              <div className="crmSection">
                <div className="crmSection-label">Atendimento</div>
                <div className="crmRow">
                  <span className="crmRow-key">Status</span>
                  <span className={`badge badge-${statusBadgeTone(conversation.status)}`} style={{ fontSize: 10, height: 20, padding: "0 8px" }}>
                    {conversationStatusLabel(conversation.status)}
                  </span>
                </div>
                <div className="crmRow"><span className="crmRow-key">Atendente</span><span className="crmRow-val">{conversation.attendant_name || "Não atribuído"}</span></div>
                <div className="crmRow">
                  <span className="crmRow-key">SLA</span>
                  <span className="crmRow-val" style={{ color: customerSla.tone === "critical" ? "var(--danger)" : "inherit" }}>{customerSla.detail || "—"}</span>
                </div>
                {conversation.sector && (
                  <div className="crmRow"><span className="crmRow-key">Setor</span><span className="crmRow-val">{conversation.sector}</span></div>
                )}
              </div>

              {/* ── TAGS ── */}
              <div className="crmTags">
                {tagItems.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className="crmTag"
                    title="Clique para remover"
                    onClick={() => removeTag(tag)}
                  >
                    {tag} <span style={{ marginLeft: 3, opacity: 0.6, fontSize: 10 }}>×</span>
                  </button>
                ))}
                {addingTag ? (
                  <div className="crmTagInput">
                    <input
                      ref={tagInputRef}
                      autoFocus
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); addTag(); }
                        if (e.key === "Escape") { setAddingTag(false); setTagInput(""); }
                      }}
                      placeholder="nova tag..."
                      disabled={savingTag}
                    />
                    <button type="button" className="crmTagConfirm" onClick={addTag} disabled={savingTag || !tagInput.trim()}>
                      <Icon name="check" size={11} />
                    </button>
                    <button type="button" className="crmTagCancel" onClick={() => { setAddingTag(false); setTagInput(""); }}>
                      <Icon name="close" size={11} />
                    </button>
                  </div>
                ) : (
                  <button type="button" className="crmTagAdd" onClick={() => { setAddingTag(true); }}>
                    <Icon name="plus" size={10} /> Adicionar
                  </button>
                )}
              </div>

              {/* ── Ações ── */}
              <div className="crmActions">
                <button className="crmActionBtn" type="button" onClick={() => onNavigate?.("contatos")}>
                  <Icon name="contact" size={13} /> Abrir CRM completo
                </button>
                {conversation.status !== "finalizado" && (
                  <button className="crmActionBtn" type="button" onClick={() => onNavigate?.("funil")}>
                    <Icon name="deal" size={13} /> Criar oportunidade
                  </button>
                )}
                <button className="crmActionBtnGhost" type="button" onClick={() => onArchive?.()}>
                  <Icon name="checkCircle" size={13} /> Arquivar conversa
                </button>
              </div>
            </>
          ) : null}

          {activeTab === "contact" && editing ? (
            <form className="sideForm" onSubmit={saveContact}>
              <label>Nome</label>
              <input value={contactForm.name} onChange={(e) => setContactField("name", e.target.value)} />
              <label>Telefone</label>
              <input value={contactForm.phone} onChange={(e) => setContactField("phone", maskBrazilPhoneInput(e.target.value))} placeholder="+55 (83) 99999-9999" />
              <label>Empresa</label>
              <input value={contactForm.company} onChange={(e) => setContactField("company", e.target.value)} />
              <label>Email</label>
              <input value={contactForm.email} onChange={(e) => setContactField("email", e.target.value)} />
              <label>Tags</label>
              <input value={contactForm.tags} onChange={(e) => setContactField("tags", e.target.value)} />
              <label>Observações</label>
              <textarea value={contactForm.notes} onChange={(e) => setContactField("notes", e.target.value)} />
              <button className="fullBtn">Salvar contato</button>
              <button type="button" className="fullBtn secondary" onClick={() => setEditing(false)}>Cancelar</button>
            </form>
          ) : null}

          {activeTab === "crm" ? (
            <div className="leadMiniBox">
              <div className="crmSectionHeader">
                <strong>Oportunidade</strong>
                <span>Transforme esse atendimento em próxima etapa comercial</span>
              </div>
              <form onSubmit={createLead} className="sideForm compactLeadForm">
                <div className="compactLeadGrid">
                  <label>Título</label>
                  <input value={leadForm.title} onChange={(e) => setLeadField("title", e.target.value)} />
                  <label>Valor</label>
                  <input type="number" value={leadForm.value} onChange={(e) => setLeadField("value", e.target.value)} placeholder="0,00" />
                  <label>Etapa</label>
                  <select value={leadForm.stage_id} onChange={(e) => setLeadField("stage_id", e.target.value)}>
                    {stages.map((stage) => <option key={stage.id} value={stage.id}>{stage.name}</option>)}
                  </select>
                  <label>Responsável</label>
                  <select value={leadForm.responsible_user_id} onChange={(e) => setLeadField("responsible_user_id", e.target.value)}>
                    <option value="">Eu mesmo</option>
                    {users.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </div>
                <button className="fullBtn">Criar oportunidade</button>
              </form>
              <div className="crmActionStack">
                <button className="secondaryBtn" type="button" onClick={() => onNavigate?.("funil")}>Abrir pipeline</button>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setActiveTab("contact")}>Voltar ao contato</button>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setActiveTab("history")}>Ver histórico</button>
              </div>
            </div>
          ) : null}

          {activeTab === "history" ? (
            <section className="crmPanelSection">
              <div className="crmSectionHeader">
                <strong>Histórico unificado</strong>
                <span>Mensagens, notas e eventos do atendimento</span>
              </div>
              <div className="historyFilterRow">
                {[
                  ["all", "Tudo"],
                  ["messages", "Mensagens"],
                  ["events", "Eventos"],
                  ["notes", "Notas"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`historyFilterChip ${historyFilter === value ? "active" : ""}`}
                    onClick={() => setHistoryFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <form className="timelineComposer" onSubmit={saveInternalNote}>
                <textarea
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  placeholder="Registrar contexto interno, próximo passo, objeção ou pendência..."
                  rows={3}
                />
                <div className="timelineComposerActions">
                  <small>Visível só no CRM do atendimento.</small>
                  <button type="submit" className="secondaryBtn" disabled={savingNote || !internalNote.trim()}>
                    {savingNote ? "Salvando..." : "Salvar nota"}
                  </button>
                </div>
              </form>
              <div className="timelineList">
                {timelineLoading && <div className="muted timelineEmpty">Carregando histórico...</div>}
                {!timelineLoading && unifiedHistory.length === 0 && (
                  <div className="muted timelineEmpty">Nenhum evento operacional registrado ainda.</div>
                )}
                {!timelineLoading && unifiedHistory.map((event) => (
                  <article key={event.id} className={`timelineCard ${event.kind === "message" ? (event.subtype === "incoming" ? "info" : "success") : (event.tone || timelineEventTone(event.subtype))}`}>
                    <div className={`timelineMarker ${event.kind === "message" ? `message-${event.subtype}` : ""}`} />
                    <div className="timelineContent">
                      <div className="timelineHeader">
                        <strong>{event.title}</strong>
                        <small>{formatTime(event.created_at)}</small>
                      </div>
                      <p>{event.body}</p>
                      {event.meta ? <small className="timelineMetaLine">{event.meta}</small> : null}
                      <span>{event.actor_name || "Sistema"}</span>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
      </div>
    </div>
  );
}

export default CustomerSidePanel;
